import type {
  RateAndReviewStatus,
  ReviewFormData,
  ReviewOption,
  ReviewableProduct,
} from '@iheartjane/menu';
import {
  INITIAL_REVIEW_FORM_DATA,
  RateAndReviewFormModal,
  RateAndReviewWizardModal,
} from '@iheartjane/menu';
import { useEffect, useState } from 'react';

import { useFetcherWithMutation } from '@jane/bloom/hooks/useFetcherWithMutation';
import { useLeaveReviewAbTest } from '@jane/bloom/hooks/useLeaveReviewAbTest';
import type { CreateProductReviewErrors } from '@jane/bloom/routes/api.product-reviews/types';
import type { LeaveReviewSource } from '@jane/bloom/services/tracking/Events';
import {
  trackLoadedWriteProductReviewForm,
  trackReviewedProduct,
} from '@jane/bloom/services/tracking/track';

import type { LeaveReviewProduct } from './types';

type ReviewSubmission = {
  activities: number[];
  comment: string;
  feelings: number[];
  productId: number;
  rating: number;
};

export interface LeaveReviewModalProps {
  activityOptions: ReviewOption[];
  feelingOptions: ReviewOption[];
  isMobile: boolean;
  moreToReview: LeaveReviewProduct[];
  onClose: () => void;
  product: LeaveReviewProduct;
  source: LeaveReviewSource;
}

const labelsFor = (ids: number[], options: ReviewOption[]) =>
  options.filter(({ id }) => ids.includes(id)).map(({ label }) => label);

const reviewErrors = (errors: unknown) =>
  (errors ?? {}) as Partial<CreateProductReviewErrors>;

const isRetryableReviewError = (errors: unknown) => {
  const { reason } = reviewErrors(errors);
  return reason === undefined || reason === 'failed';
};

export const LeaveReviewModal = ({
  activityOptions,
  feelingOptions,
  isMobile,
  moreToReview,
  onClose,
  product: initialProduct,
  source,
}: LeaveReviewModalProps) => {
  const showWizard = useLeaveReviewAbTest(true);
  const [product, setProduct] = useState(initialProduct);
  const [queue, setQueue] = useState(() =>
    moreToReview.filter(({ id }) => id !== initialProduct.id)
  );
  const [value, setValue] = useState<ReviewFormData>(INITIAL_REVIEW_FORM_DATA);
  const [status, setStatus] = useState<RateAndReviewStatus>('editing');
  const [isRetryable, setIsRetryable] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    trackLoadedWriteProductReviewForm(source);
  }, [product.id, source]);

  const { isPending, mutate } = useFetcherWithMutation<ReviewSubmission, null>({
    action: '/api/product-reviews',
  });

  const handleSubmit = () => {
    const submitted = { productId: product.id, ...value };
    mutate(submitted, {
      onError: (errors) => {
        setIsRetryable(isRetryableReviewError(errors));
        setErrorMessage(reviewErrors(errors).message);
        setStatus('failed');
      },
      onSuccess: () => {
        setStatus('submitted');
        trackReviewedProduct({
          activities: labelsFor(submitted.activities, activityOptions),
          feelings: labelsFor(submitted.feelings, feelingOptions),
          hasComment: submitted.comment.trim() !== '',
          productId: product.id,
          productName: product.name,
          rating: submitted.rating,
          source,
        });
      },
    });
  };

  const handleReviewProduct = ({ id }: ReviewableProduct) => {
    const next = queue.find((queued) => queued.id === id);
    if (!next) return;

    setProduct(next);
    setQueue(queue.filter((queued) => queued.id !== id));
    setValue(INITIAL_REVIEW_FORM_DATA);
    setStatus('editing');
  };

  const Variant = showWizard
    ? RateAndReviewWizardModal
    : RateAndReviewFormModal;

  return (
    <Variant
      activityOptions={activityOptions}
      brand={product.brand}
      category={product.category}
      errorMessage={errorMessage}
      feelingOptions={feelingOptions}
      imageUrl={product.imageUrl}
      isMobile={isMobile}
      isRetryable={isRetryable}
      isSubmitting={isPending}
      moreToReview={queue}
      onChange={setValue}
      onRequestClose={onClose}
      onReviewProduct={handleReviewProduct}
      onSubmit={handleSubmit}
      open
      productName={product.name}
      status={status}
      value={value}
    />
  );
};
